import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import AssignedDoctor from './AssignedDoctor';
import { formatInTimeZone } from 'date-fns-tz';
import {
  changeAddressModalVisibility,
  changeMedicalCardModalVisibility,
  setBiologicalAge,
  setMemberData
} from '../../../store/memberSlice';
import './MemberInfoCard.scss';
import MemberNotes from './MemberNotes/MemberNotes';
import AssignedNutritionist from './AssignedNutritionist';
import AssignedIncharge from './AssignedIncharge';
import { parseISO } from 'date-fns';
import { getMemberBioAge, updateMember, getMember } from '../../../api/members';
import { useEffect, useState } from 'react';
import CommunicationConsent from '../CommunicationConsent';
import Select, { SelectOption } from '../../BaseComponents/Select';
import { toast } from 'react-toastify';
import { ReactComponent as ChatIcon } from '../../../assets/icons/Chat.svg';
import MemberAIChatPopup from './MemberAIChatPopup';
import MemberGPSummaryPopup from './MemberGPSummaryPopup';
import MemberPaymentsModal from './MemberPaymentsModal';
import MemberTagsSelect from './MemberTagsSelect';
import DisableNotificationToggle from './DisableNotificationToggle';

const getMemberStatus = (memberData: any): string => {
  const membershipStatus = memberData?.membership_status;
  if (membershipStatus === null || membershipStatus === undefined) {
    return '-';
  }
  return membershipStatus;
};

const formatStatusForDisplay = (status: string): string => {
  if (status === '-') {
    return '-';
  }
  return status.replace(/_/g, ' ').split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

const membershipStatusOptions = [
  { key: 'user_added', value: 'User Added' },
  { key: 'user_paid', value: 'User Paid' },
  { key: 'member_active', value: 'Member Active' },
  { key: 'tuli_appt_booked', value: 'Tuli Appt Booked' },
  { key: 'tuli_appt_complete', value: 'Tuli Appt Complete' },
  { key: 'sample_received', value: 'Sample Received' },
  { key: 'report_available', value: 'Report Available' },
  { key: 'report_awaiting_review_by_gp', value: 'Report Awaiting Review By GP' },
  { key: 'report_awaiting_discussion', value: 'Report Awaiting Discussion' },
  { key: 'gp_review_booked', value: 'GP Review Booked' },
  { key: 'report_published', value: 'Report Published' },
  { key: 'nutritionist_appt_invite_sent', value: 'Nutritionist Appt Invite Sent' },
  { key: 'nutritionist_appt_booked', value: 'Nutritionist Appt Booked' },
  { key: 'care_notes_uploaded', value: 'Care Notes Uploaded' },
  { key: 'recommendations_uploaded', value: 'Recommendations Uploaded' },
  { key: 'supplements_uploaded', value: 'Supplements Uploaded' },
  { key: 'home_blood_appt_requested', value: 'Home Blood Appt Requested' },
  { key: 'home_blood_appt_booked', value: 'Home Blood Appt Booked' },
  { key: 'home_blood_appt_complete', value: 'Home Blood Appt Complete' },
  { key: 'scan_scheduled', value: 'Scan Scheduled' },
  { key: 'scan_complete', value: 'Scan complete' },
  { key: 'scan_review_invite_sent', value: 'Scan review invite sent' },
  { key: 'mri_scan_published', value: 'MRI Scan published' },
  { key: 'testing_complete_feedback', value: 'Testing complete feedback' },
  { key: 'pre_scan_consult_invite_sent', value: 'Pre-scan consultation invite sent' },
  { key: 'pre_scan_consult_booked', value: 'Pre-scan consultation booked' },
  { key: 'pre_scan_questionnaire_link_sent', value: 'Pre-scan questionnaire link sent' },
  { key: 'pre_scan_questionnaire_submitted', value: 'Pre-scan questionnaire submitted' },
  { key: 'subscription_cancelled', value: 'Subscription Cancelled' }
];

const MemberInfoCard = ({sidebarCollapsed}: {sidebarCollapsed: boolean}) => {
  const dispatch = useDispatch();
  const { memberData, biologicalAge } = useSelector((state: RootState) => state.member);
  const assigned_doctor = JSON.parse(sessionStorage.getItem("assigned_doctor") ?? "false");
  const { admin: { role } } = useSelector((state: RootState) => state.admin);
  const visible = ((role !== "doctor") || (assigned_doctor && role === "doctor"));
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [isGpSummaryOpen, setIsGpSummaryOpen] = useState(false);
  const [isPaymentsModalOpen, setIsPaymentsModalOpen] = useState(false);
  useEffect(() => {

    const fetchBioAge = async () => {

      if (memberData?.user_id) {

        try {

          const response = await getMemberBioAge(memberData?.user_id);
          const bioAgeValue = response?.biological_age?.value || "";
          dispatch(setBiologicalAge(bioAgeValue));

        } catch (error) {

          dispatch(setBiologicalAge(null));

        }

      }

    };

    fetchBioAge();

  }, [memberData?.user_id, dispatch]);

  const getSelectedStatusOption = (): SelectOption => {
    const currentStatusKey = getMemberStatus(memberData);
    const selected = membershipStatusOptions.find(
      (option) => option.key === currentStatusKey
    );
    return selected || { key: currentStatusKey, value: formatStatusForDisplay(currentStatusKey) };
  };

  const handleStatusSelect = async (option: SelectOption, name: string) => {
    if (isUpdatingStatus || !memberData?.user_id) return;

    const statusKey = option.key as string;
    if (!statusKey) return;

    setIsUpdatingStatus(true);

    try {
      await updateMember({
        user_id: memberData.user_id,
        membership_status: statusKey
      });

      toast.success('Member status updated successfully');

      // Refresh the member data
      const updatedMember = await getMember(memberData.user_id);
      dispatch(setMemberData(updatedMember));
    } catch (error: any) {
      console.error('Error updating member status:', error);
      const errorMessage = error?.response?.data?.detail || error?.response?.data?.description || 'Failed to update member status';
      toast.error(errorMessage);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const calculateAge = (dob?: string | null) => {
    if (!dob) return '-';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    if (
      today.getMonth() < birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() &&
        today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  return (
    <div className={`member-info-card-wrapper ${sidebarCollapsed ? '' : 'open'}`}>
      <div className='member-info-card'>
        <div className='member-main-info'>
          <div className='member-name-row'>
            {memberData?.tags && memberData.tags.length > 0 && (
              <div className='member-tags-badges'>
                {memberData.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className='member-tag-badge'
                    style={{ backgroundColor: tag.color || '#238698' }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
            <div className='member-name-with-chat'>
              <div className='name'>
                <span className='name-text'>
                  {`${memberData?.first_name} ${memberData?.last_name}`}
                </span>
              </div>
              {memberData?.user_id && (
                <button
                  type='button'
                  className='member-ai-chat-icon'
                  onClick={() => setIsAIChatOpen(true)}
                  aria-label='Ask about results'
                >
                  <ChatIcon />
                </button>
              )}

              {memberData?.user_id && (
                <button
                  type='button'
                  className='member-ai-gp-summary-icon'
                  onClick={() => setIsGpSummaryOpen(true)}
                  aria-label='View GP summary'
                  title='View GP summary'
                >
                  GP
                </button>
              )}
            </div>
            {visible && (
              <div className={`member-status-wrapper status-${getMemberStatus(memberData).toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-')}`}>
                {isUpdatingStatus ? (
                  <span className='status-text'>Updating...</span>
                ) : (
                  <Select
                    name='membership_status'
                    options={membershipStatusOptions}
                    selectedOption={getSelectedStatusOption()}
                    onChange={handleStatusSelect}
                    isError={false}
                    placeholder={formatStatusForDisplay(getMemberStatus(memberData))}
                  />
                )}
              </div>
            )}
            <div className='exp-date'>
              <div className='info-label'>Expiration date:</div>
              <div className='info-value'>
                <span>
                  {memberData?.subscription_end
                    ? formatInTimeZone(
                      parseISO(memberData.subscription_end + 'Z'),
                      'Europe/London',
                      'dd/MM/yyyy'
                    )
                    : '-'}
                </span>
              </div>
            </div>
            <div className='exp-date'>
              <div className='info-label'>Last login:</div>
              <div className='info-value'>
                <span>
                  {(memberData as any)?.last_login_at
                    ? formatInTimeZone(
                      parseISO((memberData as any).last_login_at),
                      'Europe/London',
                      'dd/MM/yyyy hh:mm a'
                    )
                    : ''}
                </span>
              </div>
            </div>
          </div>

            {visible && <DisableNotificationToggle />}
         {visible && <MemberTagsSelect />}
          <AssignedDoctor
            doctorData={memberData?.doctor ? memberData.doctor : null}
          />
          <AssignedNutritionist
            nutritionistData={
              memberData?.nutritionist ? memberData.nutritionist : null
            }
          />
          <AssignedIncharge inchargeData={memberData?.in_charge_admin} />
          <div className='main-member-info'>
            <div className='member-info'>
              <div className='info-label'>Contact Info</div>
              <div className='info-value contacts'>
                <a
                  className='mailto-link'
                  href={`mailto:${memberData?.email}`}
                  title={memberData?.email}
                >
                  {memberData?.email}
                </a>
                <span title={memberData?.phone}>{memberData?.phone}</span>
              </div>
            </div>
            <div className='divider'></div>

            <div
              className='member-info'
              onClick={() => { visible ? dispatch(changeAddressModalVisibility(true)) : console.log("clicked") }}
              style={{ cursor: 'pointer' }}
            >
              <div className='info-label'>Address</div>
              <div className='info-value address'>
                <div>
                  {memberData?.address_line_1 ? (
                    [
                      memberData?.address_line_1,
                      memberData?.address_line_2,
                      memberData?.city,
                      memberData?.country,
                      memberData?.post_code
                    ]
                      .filter(Boolean)
                      .join(', ')
                  ) : (
                    <div className='set-address'>Set Address</div>
                  )}
                </div>
              </div>
            </div>
            <div
              className='member-info'
              onClick={() => dispatch(changeAddressModalVisibility(true))}
              style={{ cursor: 'pointer' }}
            >
              <div className='info-label'>Biological age</div>
              <div className='info-value address'>
                <div>
                  {biologicalAge}
                </div>
              </div>
            </div>
            <div
              className='member-info'
              onClick={() => dispatch(changeAddressModalVisibility(true))}
              style={{ cursor: 'pointer' }}
            >
              <div className='info-label'>Age</div>
              <div className='info-value address'>
                <div>
                  {calculateAge(memberData?.dob) ?? '-'}
                </div>
              </div>
            </div>
          </div>
          <div className='member-action-buttons'>
            <div
              className='more-info-btn'
              onClick={() => dispatch(changeMedicalCardModalVisibility(true))}
            >
              Member’s Information
            </div>
            <div
              className='more-info-btn'
              onClick={() => setIsPaymentsModalOpen(true)}
            >
              Payments
            </div>
          </div>
        </div>
        <MemberNotes />
        {(memberData as any)?.membership_type && (
          <div className='plan-info-block'>
            <div className='row'>
              <span className='label'>Plan</span>
              <span className='value'>{(memberData as any)?.membership_type}</span>
            </div>
            {((memberData as any)?.membership_type?.toLowerCase() === 'core' ||
              (memberData as any)?.membership_type?.toLowerCase() === 'plus') &&
              (memberData as any)?.number_of_blood_test_plan !== undefined &&
              (
                <div className='row'>
                  <span className='label'>Number of tests</span>
                  <span className='value'>
                    {(memberData as any)?.number_of_blood_test_plan}
                  </span>
                </div>
              )}
          </div>
        )}
        {memberData?.user_id && (
          <>
            <CommunicationConsent
              memberId={memberData.user_id}
              disclaimerViewedAt={(memberData as any)?.disclaimer_viewed_at ?? null}
            />
          </>
        )}
      </div>
      <MemberAIChatPopup
        isOpen={isAIChatOpen}
        onClose={() => setIsAIChatOpen(false)}
        memberId={memberData?.user_id ?? ''}
        memberName={memberData ? `${memberData.first_name ?? ''} ${memberData.last_name ?? ''}`.trim() : undefined}
      />

      <MemberGPSummaryPopup
        isOpen={isGpSummaryOpen}
        onClose={() => setIsGpSummaryOpen(false)}
        memberId={memberData?.user_id ?? ''}
        memberName={memberData ? `${memberData.first_name ?? ''} ${memberData.last_name ?? ''}`.trim() : undefined}
      />

      <MemberPaymentsModal
        isOpen={isPaymentsModalOpen}
        onClose={() => setIsPaymentsModalOpen(false)}
        memberId={memberData?.user_id}
      />
    </div>
  );
};

export default MemberInfoCard;
